import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { ref, push, set, onValue, off, remove, get } from 'firebase/database';
import { auth, database } from '../config/firebase';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import './Admin.css';

// FontSize extension
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

interface Article {
  id?: string;
  title: string;
  content: string;
  description?: string;
  author: string;
  date: string;
}

const Admin: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Makale formu state'leri
  const [articleTitle, setArticleTitle] = useState('');
  const [articleDescription, setArticleDescription] = useState('');
  const [articleLanguage, setArticleLanguage] = useState<'en' | 'ru' | 'ar'>('en');
  const [submitting, setSubmitting] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState<string | null>(null);
  
  // Makale listesi
  const [articles, setArticles] = useState<{ [key: string]: Article }>({});
  const [showArticleList, setShowArticleList] = useState(false);
  
  // Dil bazında makale sayıları
  const [articleCounts, setArticleCounts] = useState<{ [key: string]: number }>({
    en: 0,
    ru: 0,
    ar: 0
  });
  
  // Admin sayısı kontrolü
  const [adminCount, setAdminCount] = useState<number>(0);

  // Konfigüratör (Tasarım) talepleri
  const [requests, setRequests] = useState<{ [key: string]: any }>({});
  const [showRequests, setShowRequests] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle.configure({}),
      FontSize,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      FontFamily,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[400px]',
      },
    },
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    // Admin sayısını kontrol et
    const adminsRef = ref(database, 'admins');
    onValue(adminsRef, (snapshot) => {
      const admins = snapshot.val();
      if (admins) {
        const count = Object.keys(admins).length;
        setAdminCount(count);
        // Eğer admin varsa, varsayılan olarak giriş yap sekmesini aç
        if (count > 0) {
          setIsSignUp(false);
        }
      } else {
        setAdminCount(0);
      }
    });

    return () => {
      unsubscribe();
      off(adminsRef);
    };
  }, []);

  useEffect(() => {
    if (user) {
      const articlesRef = ref(database, `technicalArticles/${articleLanguage}`);
      
      onValue(
        articlesRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setArticles(data);
          } else {
            setArticles({});
          }
        },
        (err) => {
          console.error('Error fetching articles:', err);
        }
      );

      return () => {
        off(articlesRef);
      };
    }
  }, [user, articleLanguage]);

  // Konfigüratör taleplerini çek
  useEffect(() => {
    if (user) {
      const requestsRef = ref(database, 'configuratorRequests');
      onValue(
        requestsRef,
        (snapshot) => {
          setRequests(snapshot.val() || {});
        },
        (err) => {
          console.error('Error fetching requests:', err);
        }
      );
      return () => {
        off(requestsRef);
      };
    }
  }, [user]);

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('Bu talebi silmek istediğinize emin misiniz?')) return;
    try {
      await remove(ref(database, `configuratorRequests/${requestId}`));
    } catch (err) {
      console.error('Talep silinemedi:', err);
    }
  };

  // Tüm dillerdeki makale sayılarını çek
  useEffect(() => {
    if (user) {
      const languages = ['en', 'ru', 'ar'];
      const counts: { [key: string]: number } = { en: 0, ru: 0, ar: 0 };
      let completed = 0;

      languages.forEach((lang) => {
        const articlesRef = ref(database, `technicalArticles/${lang}`);
        onValue(
          articlesRef,
          (snapshot) => {
            const data = snapshot.val();
            counts[lang] = data ? Object.keys(data).length : 0;
            completed++;
            if (completed === languages.length) {
              setArticleCounts(counts);
            }
          },
          (err) => {
            console.error(`Error fetching articles for ${lang}:`, err);
            completed++;
            if (completed === languages.length) {
              setArticleCounts(counts);
            }
          }
        );
      });

      return () => {
        languages.forEach((lang) => {
          const articlesRef = ref(database, `technicalArticles/${lang}`);
          off(articlesRef);
        });
      };
    }
  }, [user]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (isSignUp) {
        // Admin sayısını Firebase'den kontrol et
        const adminsRef = ref(database, 'admins');
        const snapshot = await get(adminsRef);
        const admins = snapshot.val();
        const currentAdminCount = admins ? Object.keys(admins).length : 0;
        
        if (currentAdminCount > 0) {
          setError('Zaten bir admin kullanıcı mevcut. Yeni admin kaydı yapılamaz.');
          return;
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // İlk admin'i Firebase'e kaydet
        const adminRef = ref(database, `admins/${userCredential.user.uid}`);
        await set(adminRef, {
          email: email,
          createdAt: new Date().toISOString()
        });
        setSuccess('Kayıt başarılı! Hoş geldiniz.');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        setSuccess('Giriş başarılı!');
      }
      setEmail('');
      setPassword('');
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setSuccess('Çıkış yapıldı.');
    } catch (err: any) {
      setError(err.message || 'Çıkış yapılırken bir hata oluştu.');
    }
  };

  const handleSubmitArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    if (!user) {
      setError('Kullanıcı giriş yapmamış. Lütfen giriş yapın.');
      setSubmitting(false);
      return;
    }

    if (!editor) {
      setError('Editor yüklenmedi.');
      setSubmitting(false);
      return;
    }

    const content = editor.getHTML();

    if (!articleTitle.trim() || !content.trim() || content.trim() === '<p></p>') {
      setError('Başlık ve içerik alanları zorunludur.');
      setSubmitting(false);
      return;
    }

    try {
      const article: Article = {
        title: articleTitle.trim(),
        content: content,
        description: articleDescription.trim() || undefined,
        author: '',
        date: editingArticleId ? articles[editingArticleId].date : new Date().toISOString(),
      };

      if (editingArticleId) {
        // Düzenleme
        const articleRef = ref(database, `technicalArticles/${articleLanguage}/${editingArticleId}`);
        await set(articleRef, article);
        setSuccess('Makale başarıyla güncellendi!');
      } else {
        // Yeni makale
        const articlesRef = ref(database, `technicalArticles/${articleLanguage}`);
        const newArticleRef = push(articlesRef);
        await set(newArticleRef, article);
        setSuccess('Makale başarıyla kaydedildi!');
      }
      
      setArticleTitle('');
      setArticleDescription('');
      editor.commands.clearContent();
      setEditingArticleId(null);
    } catch (err: any) {
      console.error('Makale kaydetme hatası:', err);
      setError(err.message || 'Makale kaydedilirken bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditArticle = (articleId: string) => {
    const article = articles[articleId];
    if (article && editor) {
      setArticleTitle(article.title);
      setArticleDescription(article.description || '');
      editor.commands.setContent(article.content);
      setEditingArticleId(articleId);
      setShowArticleList(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDeleteArticle = async (articleId: string) => {
    if (!window.confirm('Bu makaleyi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const articleRef = ref(database, `technicalArticles/${articleLanguage}/${articleId}`);
      await remove(articleRef);
      setSuccess('Makale başarıyla silindi!');
      
      if (editingArticleId === articleId) {
        setArticleTitle('');
        if (editor) {
          editor.commands.clearContent();
        }
        setEditingArticleId(null);
      }
    } catch (err: any) {
      setError(err.message || 'Makale silinirken bir hata oluştu.');
    }
  };

  const handleNewArticle = () => {
    setArticleTitle('');
    setArticleDescription('');
    if (editor) {
      editor.commands.clearContent();
    }
    setEditingArticleId(null);
    setShowArticleList(false);
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="auth-card">
            <h1>Admin Girişi</h1>
            {adminCount === 0 && (
              <div className="auth-tabs">
                <button
                  className={isSignUp ? 'active' : ''}
                  onClick={() => {
                    setIsSignUp(true);
                    setError('');
                    setSuccess('');
                  }}
                >
                  Kayıt Ol
                </button>
                <button
                  className={!isSignUp ? 'active' : ''}
                  onClick={() => {
                    setIsSignUp(false);
                    setError('');
                    setSuccess('');
                  }}
                >
                  Giriş Yap
                </button>
              </div>
            )}
            {adminCount > 0 && (
              <div className="auth-tabs">
                <button className="active" style={{ width: '100%', cursor: 'default' }}>
                  Giriş Yap
                </button>
              </div>
            )}

            <form onSubmit={handleAuth} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">E-posta</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="ornek@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Şifre</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="En az 6 karakter"
                  minLength={6}
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <button type="submit" className="submit-btn">
                {isSignUp ? 'Kayıt Ol' : 'Giriş Yap'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const articlesArray = Object.entries(articles).map(([id, article]) => ({
    id,
    ...article,
  })).sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  const requestsArray = Object.entries(requests).map(([id, req]) => ({
    id,
    ...(req as any),
  })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (!editor) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading">Editor yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Paneli</h1>
          <div className="user-info">
            <span>Hoş geldiniz, {user.email}</span>
            <button onClick={handleLogout} className="logout-btn">
              Çıkış Yap
            </button>
          </div>
        </div>

        {/* Makale İstatistikleri */}
        <div className="article-stats">
          <h2>Makale İstatistikleri</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">English</div>
              <div className="stat-value">{articleCounts.en}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Русский</div>
              <div className="stat-value">{articleCounts.ru}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">العربية</div>
              <div className="stat-value">{articleCounts.ar}</div>
            </div>
          </div>
        </div>

        <div className="admin-tabs">
          <button
            className={!showArticleList && !showRequests ? 'active' : ''}
            onClick={() => { setShowArticleList(false); setShowRequests(false); }}
          >
            {editingArticleId ? 'Makale Düzenle' : 'Yeni Makale'}
          </button>
          <button
            className={showArticleList && !showRequests ? 'active' : ''}
            onClick={() => { setShowArticleList(true); setShowRequests(false); }}
          >
            Makaleleri Yönet ({articlesArray.length})
          </button>
          <button
            className={showRequests ? 'active' : ''}
            onClick={() => setShowRequests(true)}
          >
            Tasarım Talepleri ({requestsArray.length})
          </button>
        </div>

        {showRequests ? (
          <div className="requests-management">
            <h2>Salon Tasarım Talepleri</h2>
            {requestsArray.length === 0 ? (
              <div className="no-articles">
                <p>Henüz tasarım talebi bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="requests-list">
                {requestsArray.map((req) => (
                  <div key={req.id} className={`request-card ${req.status === 'new' ? 'is-new' : ''}`}>
                    <div className="request-card-head">
                      <div>
                        <h3>{req.contact?.name || 'İsimsiz'}</h3>
                        <div className="request-contact">
                          <a href={`mailto:${req.contact?.email}`}>{req.contact?.email}</a>
                          <span>·</span>
                          <a href={`tel:${req.contact?.phone}`}>{req.contact?.phone}</a>
                        </div>
                      </div>
                      <div className="request-budget">
                        <span>Tahmini Bütçe</span>
                        <strong>${Number(req.estimatedBudget || 0).toLocaleString('en-US')}</strong>
                      </div>
                    </div>
                    <div className="request-selections">
                      {req.selections && Object.entries(req.selections).map(([key, val]) => (
                        <div className="request-sel" key={key}>
                          <span className="request-sel-key">{key}</span>
                          <span className="request-sel-val">{String(val)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="request-card-foot">
                      <span className="request-date">{req.createdAt ? formatDate(req.createdAt) : ''}</span>
                      <button className="delete-btn" onClick={() => handleDeleteRequest(req.id)}>Sil</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : showArticleList ? (
          <div className="articles-management">
            <h2>Makale Yönetimi</h2>
            {articlesArray.length === 0 ? (
              <div className="no-articles">
                <p>Henüz makale bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="articles-list">
                {articlesArray.map((article) => (
                  <div key={article.id} className="article-item">
                    <div className="article-item-header">
                      <h3>{article.title}</h3>
                      <div className="article-item-meta">
                        <span>{formatDate(article.date)}</span>
                      </div>
                    </div>
                    <div className="article-item-actions">
                      <button
                        onClick={() => handleEditArticle(article.id!)}
                        className="edit-btn"
                      >
                        Düzenle
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article.id!)}
                        className="delete-btn"
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="article-form-card">
            <div className="form-header">
              <h2>{editingArticleId ? 'Makale Düzenle' : 'Yeni Teknik Makale Ekle'}</h2>
              {editingArticleId && (
                <button onClick={handleNewArticle} className="new-article-btn">
                  Yeni Makale
                </button>
              )}
            </div>
            <form onSubmit={handleSubmitArticle} className="article-form">
              <div className="form-group">
                <label htmlFor="articleLanguage">Makale Dili</label>
                <select
                  id="articleLanguage"
                  value={articleLanguage}
                  onChange={(e) => {
                    setArticleLanguage(e.target.value as 'en' | 'ru' | 'ar');
                    setEditingArticleId(null);
                    setArticleTitle('');
                    setArticleDescription('');
                    if (editor) {
                      editor.commands.clearContent();
                    }
                  }}
                  className="form-select"
                >
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                  <option value="ar">العربية</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="articleTitle">Makale Başlığı</label>
                <input
                  type="text"
                  id="articleTitle"
                  value={articleTitle}
                  onChange={(e) => setArticleTitle(e.target.value)}
                  required
                  placeholder="Makale başlığını girin"
                />
              </div>

              <div className="form-group">
                <label htmlFor="articleDescription">Makale Açıklaması</label>
                <textarea
                  id="articleDescription"
                  value={articleDescription}
                  onChange={(e) => setArticleDescription(e.target.value)}
                  placeholder="Makale açıklamasını girin (makale listesinde görünecek)"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Makale İçeriği</label>
                <div className="editor-toolbar word-like-toolbar">
                  {/* Font ve Boyut Grubu */}
                  <div className="toolbar-group">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          editor.chain().focus().setFontFamily(e.target.value).run();
                        } else {
                          editor.chain().focus().unsetFontFamily().run();
                        }
                      }}
                      title="Font Ailesi"
                      className="font-select"
                    >
                      <option value="">Font</option>
                      <option value="Arial">Arial</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Calibri">Calibri</option>
                      <option value="Comic Sans MS">Comic Sans MS</option>
                    </select>
                    <select
                      onChange={(e) => {
                        const size = e.target.value;
                        if (size) {
                          editor.chain().focus().setFontSize(`${size}pt`).run();
                        } else {
                          editor.chain().focus().unsetFontSize().run();
                        }
                      }}
                      title="Font Boyutu"
                      className="font-size-select"
                    >
                      <option value="">Boyut</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="14">14</option>
                      <option value="16">16</option>
                      <option value="18">18</option>
                      <option value="20">20</option>
                      <option value="24">24</option>
                      <option value="28">28</option>
                      <option value="32">32</option>
                      <option value="36">36</option>
                      <option value="48">48</option>
                      <option value="72">72</option>
                    </select>
                  </div>

                  <div className="toolbar-divider"></div>

                  {/* Formatlama Butonları */}
                  <div className="toolbar-group">
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                      title="Kalın (Ctrl+B)"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                      title="İtalik (Ctrl+I)"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
                      title="Altı Çizili (Ctrl+U)"
                    >
                      <u>U</u>
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  {/* Hizalama Butonları */}
                  <div className="toolbar-group">
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().setTextAlign('left').run()}
                      className={`toolbar-btn align-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                      title="Sola Hizala"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 2h12v1H2V2zm0 3h8v1H2V5zm0 3h12v1H2V8zm0 3h8v1H2v-1z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().setTextAlign('center').run()}
                      className={`toolbar-btn align-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                      title="Ortala"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 2h12v1H2V2zm2 3h8v1H4V5zm-2 3h12v1H2V8zm2 3h8v1H4v-1z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().setTextAlign('right').run()}
                      className={`toolbar-btn align-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                      title="Sağa Hizala"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 2h12v1H2V2zm4 3h8v1H6V5zm-4 3h12v1H2V8zm4 3h8v1H6v-1z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                      className={`toolbar-btn align-btn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                      title="İki Yana Yasla"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 2h12v1H2V2zm0 3h12v1H2V5zm0 3h12v1H2V8zm0 3h12v1H2v-1z"/>
                      </svg>
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  {/* Liste Butonları */}
                  <div className="toolbar-group">
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                      title="Madde İşareti"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <circle cx="4" cy="4" r="1.5"/>
                        <circle cx="4" cy="8" r="1.5"/>
                        <circle cx="4" cy="12" r="1.5"/>
                        <path d="M7 4h9M7 8h9M7 12h9" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                      title="Numaralı Liste"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <text x="2" y="5" fontSize="10" fill="currentColor">1.</text>
                        <text x="2" y="9" fontSize="10" fill="currentColor">2.</text>
                        <text x="2" y="13" fontSize="10" fill="currentColor">3.</text>
                        <path d="M6 4h9M6 8h9M6 12h9" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  {/* Renk ve Diğer Özellikler */}
                  <div className="toolbar-group">
                    <input
                      type="color"
                      onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                      title="Metin Rengi"
                      className="color-picker"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const url = window.prompt('Link URL:');
                        if (url) {
                          editor.chain().focus().setLink({ href: url }).run();
                        }
                      }}
                      className={`toolbar-btn ${editor.isActive('link') ? 'active' : ''}`}
                      title="Link Ekle"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                        <path d="M7 8a3 3 0 0 1 3-3h0a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3h0a3 3 0 0 1-3-3" strokeWidth="1.5"/>
                        <path d="M9 8a3 3 0 0 1-3 3h0a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3h0a3 3 0 0 1 3 3" strokeWidth="1.5"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = window.prompt('Görsel URL:');
                        if (url) {
                          editor.chain().focus().setImage({ src: url }).run();
                        }
                      }}
                      className="toolbar-btn"
                      title="Görsel Ekle"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                        <rect x="2" y="2" width="12" height="12" rx="1" strokeWidth="1.5"/>
                        <circle cx="5.5" cy="5.5" r="1.5" strokeWidth="1.5"/>
                        <path d="M2 10l3-3 3 3 4-4 2 2v2H2v-2z" strokeWidth="1.5"/>
                      </svg>
                    </button>
                  </div>

                  <div className="toolbar-divider"></div>

                  {/* Geri Al / Yinele */}
                  <div className="toolbar-group">
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().undo().run()}
                      className="toolbar-btn"
                      title="Geri Al (Ctrl+Z)"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                        <path d="M3 8h10M6 5l-3 3 3 3" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor.chain().focus().redo().run()}
                      className="toolbar-btn"
                      title="Yinele (Ctrl+Y)"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                        <path d="M13 8H3M10 5l3 3-3 3" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="editor-wrapper">
                  <EditorContent editor={editor} />
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? 'Kaydediliyor...' : editingArticleId ? 'Makaleyi Güncelle' : 'Makaleyi Kaydet'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
